import React from "react";
import { Link } from "react-router-dom";
import { Edit } from "lucide-react";
import { Badge } from "src/shadcn/ui/badge";
import { DD_MM_YYYY } from "src/helpers";
import { VendorColumnHeader, Incoterms } from "src/enums";
import { ROUTES_CONFIG } from "src/constants/routesConfig";
import { INCO_TERMS_OPTIONS, COUNTRY_OF_EXPORT_OPTIONS } from "src/constants";
import { Table } from "src/components";
import { TableColumn, Vendor } from "src/types";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "src/shadcn/ui/tooltip";

interface VendorsDashTableProps {
  vendors: Vendor[];
  columnsToHide: VendorColumnHeader[];
}

const displayOrDash = (value?: string) => (value && value.trim().length > 0 ? value : "-");

const formatDateOrDash = (value?: string) => (value ? DD_MM_YYYY.formatDateAndAppendTime(new Date(value)) : undefined);

const chipClass =
  "inline-flex items-center rounded-full bg-secondary-100 px-2 py-0.5 text-xs font-medium text-secondary-900";

const renderBooleanBadge = (value?: boolean) => {
  const displayValue = value === true;
  return (
    <Badge variant={displayValue ? "success" : "secondary"} className="font-medium">
      {displayValue ? "Yes" : "No"}
    </Badge>
  );
};

const VendorsDashTable: React.FC<VendorsDashTableProps> = ({ vendors, columnsToHide }) => {
  const centerStyles = { textAlign: "center" as const };
  const wrapStyles = { ...centerStyles, whiteSpace: "nowrap" as const };

  const vendorsTableColumns: TableColumn<VendorColumnHeader, Vendor>[] = [
    {
      header: VendorColumnHeader.NAME,
      column: (row) => <div className="font-medium text-center">{displayOrDash(row.name)}</div>,
      styles: centerStyles
    },
    {
      header: VendorColumnHeader.LOCATION,
      column: (row) => <span className="text-sm">{displayOrDash(row.location)}</span>,
      styles: centerStyles
    },
    {
      header: VendorColumnHeader.PORTAL_ORDER,
      column: (row) => renderBooleanBadge(row.portalOrderVendor),
      styles: centerStyles
    },
    {
      header: VendorColumnHeader.COLLECTION_ORDER,
      column: (row) => renderBooleanBadge(row.collectionOrderVendor),
      styles: centerStyles
    },
    {
      header: VendorColumnHeader.COLLECTION_TYPE,
      column: (row) => <span className="text-sm">{displayOrDash(row.collectionType)}</span>,
      styles: centerStyles
    },
    {
      header: VendorColumnHeader.PORTAL_ACCESS,
      column: (row) => renderBooleanBadge(row.portalAccess),
      styles: centerStyles
    },
    {
      header: VendorColumnHeader.FILE_TYPE,
      column: (row) => <span className="text-sm">{row.fileType || "PDF"}</span>,
      styles: centerStyles
    },
    {
      header: VendorColumnHeader.IS_PRO_FORMA,
      column: (row) => renderBooleanBadge(row.isProForma),
      styles: centerStyles
    },
    {
      header: VendorColumnHeader.EMAILS,
      column: (row) => {
        const emails = row.emails?.filter(Boolean) ?? [];
        if (!emails.length) {
          return <span className="text-muted-foreground text-center w-full block">-</span>;
        }
        return (
          <div className="flex flex-wrap gap-1 justify-center max-w-[260px] mx-auto">
            {emails.map((email) => (
              <span key={email} className={chipClass}>
                {email}
              </span>
            ))}
          </div>
        );
      },
      styles: centerStyles
    },
    {
      header: VendorColumnHeader.INVOICE_NAME,
      column: (row) => <span className="text-sm">{displayOrDash(row.invoiceName)}</span>,
      styles: centerStyles
    },
    {
      header: VendorColumnHeader.PAYMENT_TERMS,
      column: (row) => <span className="text-sm">{displayOrDash(row.paymentTerms)}</span>,
      styles: centerStyles
    },
    {
      header: VendorColumnHeader.INCO_TERMS,
      column: (row) => {
        const incoTerms = row.incoTerms || Incoterms.EXW;
        const option = INCO_TERMS_OPTIONS.find((opt) => opt.value === incoTerms);
        if (!option) {
          return <span className="text-sm">{incoTerms}</span>;
        }
        const dashIdx = option.label.indexOf(" - ");
        const code = dashIdx >= 0 ? option.label.slice(0, dashIdx).trim() : option.value;
        return (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="inline-flex rounded-md bg-primary-100 px-2 py-0.5 text-xs font-semibold text-primary-800 ring-1 ring-primary-200/60 cursor-help">
                  {code}
                </span>
              </TooltipTrigger>
              <TooltipContent>
                <p>{option.label}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        );
      },
      styles: centerStyles
    },
    {
      header: VendorColumnHeader.COUNTRY_OF_EXPORT,
      column: (row) => {
        const codes = row.countryOfExport;
        if (codes == null || !codes.length) {
          return (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="inline-flex rounded-md bg-secondary-100 px-2 py-0.5 text-xs font-semibold text-secondary-500 ring-1 ring-secondary-200/60 cursor-help">
                    N/A
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Not set</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          );
        }
        return (
          <div className="flex flex-wrap gap-1 justify-center items-center max-w-[150px] mx-auto">
            {codes.map((code) => {
              const opt = COUNTRY_OF_EXPORT_OPTIONS.find((o) => o.value === code);
              return (
                <TooltipProvider key={code}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="inline-flex rounded-md bg-secondary-100 px-2 py-0.5 text-xs font-semibold text-secondary-800 ring-1 ring-secondary-200/60 cursor-help">
                        {code}
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>{opt?.label ?? code}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              );
            })}
          </div>
        );
      },
      styles: centerStyles
    },
    {
      header: VendorColumnHeader.CREATED_BY_EMAIL,
      column: (row) => <span className="text-sm">{displayOrDash(row.createdByUserEmail)}</span>,
      styles: centerStyles
    },
    {
      header: VendorColumnHeader.CREATED_AT,
      column: (row) => {
        const formatted = formatDateOrDash(row.createdAt);
        return <span className="text-sm text-muted-foreground">{formatted ?? "-"}</span>;
      },
      styles: wrapStyles
    },
    {
      header: VendorColumnHeader.UPDATED_AT,
      column: (row) => {
        const formatted = formatDateOrDash(row.updatedAt);
        return <span className="text-sm text-muted-foreground">{formatted ?? "-"}</span>;
      },
      styles: wrapStyles
    },
    {
      header: VendorColumnHeader.ACTIONS,
      column: (row) => (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Link
                to={`${ROUTES_CONFIG.updateVendor.path}?name=${encodeURIComponent(row.name)}`}
                className="inline-flex items-center justify-center rounded-md border border-secondary-200 bg-white px-2 py-1 text-xs font-semibold text-secondary-700 transition-colors hover:bg-secondary-50"
                aria-label={`Edit ${row.name}`}
              >
                <Edit className="h-4 w-4" />
              </Link>
            </TooltipTrigger>
            <TooltipContent>
              <p>Edit Vendor</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ),
      styles: centerStyles
    }
  ];

  return (
    <Table<VendorColumnHeader, Vendor> data={vendors} columns={vendorsTableColumns} columnsToHide={columnsToHide} />
  );
};

export { VendorsDashTable };
